import { ApiError, type WaiverLinkResult, createCustomerWaiverLink } from '@/lib/api';
import { Check, Copy, Mail, MessageSquare, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface SendWaiverLinkDrawerProps {
  customerId: string;
  customerFirstName: string;
  hasEmail: boolean;
  hasPhone: boolean;
  open: boolean;
  onClose: () => void;
}

/**
 * "Send waiver link" — mints a 7-day sign-by-link token for the customer
 * (POST /api/customers/:id/waiver-link) and offers copy / email / SMS.
 *
 * Copy-link mints WITHOUT sending; the email/SMS buttons mint a fresh link
 * and ask the server to deliver it through the comms pipeline. Server-side
 * gate is `customers.edit`; a 403 surfaces as an inline message.
 */
export function SendWaiverLinkDrawer({
  customerId,
  customerFirstName,
  hasEmail,
  hasPhone,
  open,
  onClose,
}: SendWaiverLinkDrawerProps) {
  const [link, setLink] = useState<WaiverLinkResult | null>(null);
  const [busy, setBusy] = useState<'copy' | 'email' | 'sms' | null>(null);
  const [copied, setCopied] = useState(false);
  const [sentVia, setSentVia] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setLink(null);
      setBusy(null);
      setCopied(false);
      setSentVia(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  function describeError(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 403) return 'You do not have permission to send waiver links.';
      if (err.code === 'no_waiver_configured') {
        return 'No waiver is configured yet. Author one at Settings › Waiver first.';
      }
      if (err.code === 'waiver_links_not_configured') {
        return 'Waiver links are not configured on this deployment.';
      }
      return `Could not create the link (${err.status}).`;
    }
    return (err as Error).message ?? 'Could not create the link.';
  }

  async function mint(send: false): Promise<WaiverLinkResult | null>;
  async function mint(send: true, channel: 'email' | 'sms'): Promise<WaiverLinkResult | null>;
  async function mint(send: boolean, channel?: 'email' | 'sms') {
    setError(null);
    try {
      const res = await createCustomerWaiverLink(
        customerId,
        send && channel ? { send: true, channels: [channel] } : { send: false },
      );
      setLink(res);
      return res;
    } catch (err) {
      setError(describeError(err));
      return null;
    }
  }

  async function handleCopy() {
    setBusy('copy');
    setCopied(false);
    try {
      const res = link ?? (await mint(false));
      if (!res) return;
      try {
        await navigator.clipboard.writeText(res.url);
        setCopied(true);
      } catch {
        // Clipboard blocked (insecure context / permissions) — the URL is
        // shown read-only below so staff can select + copy manually.
        setError('Could not copy automatically — select the link below to copy it.');
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleSend(channel: 'email' | 'sms') {
    setBusy(channel);
    setSentVia(null);
    try {
      const res = await mint(true, channel);
      if (!res) return;
      if (res.sent) setSentVia(res.channels);
      else
        setError(
          `Could not send by ${channel === 'sms' ? 'SMS' : 'email'} — no contact details on file.`,
        );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Send waiver link"
      className="fixed inset-0 z-50 grid place-items-center bg-foreground/40 p-4"
    >
      <div className="w-full max-w-md space-y-4 rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Waiver
            </p>
            <h2 className="font-heading text-lg font-medium">Send waiver link</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-muted-foreground hover:bg-secondary press"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <p className="text-sm leading-relaxed text-muted-foreground">
          {customerFirstName} can open the link on their phone, read your current waiver and sign by
          typing their name. No portal sign-in is needed. The link lasts 7 days and stops working if
          you publish a newer waiver.
        </p>

        <div className="grid gap-2">
          <button
            type="button"
            onClick={() => void handleCopy()}
            disabled={busy !== null}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50 press"
          >
            {copied ? (
              <Check className="size-4 text-success" strokeWidth={1.75} />
            ) : (
              <Copy className="size-4" strokeWidth={1.75} />
            )}
            {busy === 'copy' ? 'Creating link…' : copied ? 'Link copied' : 'Copy link'}
          </button>
          <button
            type="button"
            onClick={() => void handleSend('email')}
            disabled={busy !== null || !hasEmail}
            title={hasEmail ? undefined : 'No email address on file'}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50 press"
          >
            <Mail className="size-4" strokeWidth={1.75} />
            {busy === 'email' ? 'Sending…' : 'Send by email'}
          </button>
          <button
            type="button"
            onClick={() => void handleSend('sms')}
            disabled={busy !== null || !hasPhone}
            title={hasPhone ? undefined : 'No mobile number on file'}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50 press"
          >
            <MessageSquare className="size-4" strokeWidth={1.75} />
            {busy === 'sms' ? 'Sending…' : 'Send by SMS'}
          </button>
        </div>

        {link && (
          <div className="space-y-1">
            <label
              htmlFor="waiver-link-url"
              className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Link (expires {new Date(link.expiresAt).toLocaleDateString('en-GB')})
            </label>
            <input
              id="waiver-link-url"
              readOnly
              value={link.url}
              onFocus={(e) => e.currentTarget.select()}
              className="num w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs"
            />
          </div>
        )}

        {sentVia && sentVia.length > 0 && (
          <p role="status" className="flex items-center gap-1.5 text-sm text-success">
            <Check className="size-4" strokeWidth={1.75} />
            Sent by {sentVia.map((c) => (c === 'sms' ? 'SMS' : 'email')).join(' and ')}.
          </p>
        )}

        {error && (
          <p
            role="alert"
            className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
